import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { orderBy } from 'lodash';
import { toast } from 'react-toastify';

import * as Core from '../../../core';
import {
    parseQuizWhizzerTitleMetadata,
    quizWhizzerMatchGameNeedsAccessCode,
} from '../quizWhizzer/quizWhizzerAccessCodeSetup';
import { Avatar, HollowButton, SolidButton, StatusIndicator } from '../../../components/buttons-visuals';
import { ContentContainer } from '../../../components/containers';
import { TextInput } from '../../../components/inputs';
import withLoading, { WithLoadingProps } from '../../../components/withLoading';
import { useLeague, useUserPermissionService } from '../../../hooks/store';
import { MatchService } from '../../../services/matchService';
import PlatformEventsService from '../../../services/platformEventsService';

import './index.scss';

const MATCH_GAME_METADATA_UPDATED = 'MatchGameMetadataUpdated';
const MATCH_GAME_RESULTS_SUBMITTED = 'MatchGameResultsSubmitted';
const MATCH_RESULTS_SUBMITTED = 'MatchResultsSubmitted';

interface ClassSessionGamePanelProps extends WithLoadingProps {
    canConfigureAsHost: boolean;
    canImportScores: boolean;
    isMyMatch: boolean;
    match: Core.Models.MatchDetails;
    matchParticipants: Core.Models.MatchParticipant[];
    reloadMatchData: () => Promise<void>;
    reloadMatchParticipants: () => Promise<void>;
}

const getParticipantDisplayName = (participant: Core.Models.MatchParticipant): string => {
    const user = orderBy(participant.users, (u) => u.sortOrder)[0];
    if (user) {
        return Core.Identity.renderMemberName(user) || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    }
    return participant.name || 'Student';
};

const ClassSessionGamePanel = (props: ClassSessionGamePanelProps): JSX.Element => {
    const {
        canConfigureAsHost,
        canImportScores,
        isMyMatch,
        match,
        matchParticipants,
        reloadMatchData,
        reloadMatchParticipants,
        setError,
        setIsLoading,
    } = props;

    const league = useLeague();
    const userPermissionService = useUserPermissionService();
    const userId = userPermissionService.getUserId();

    const [matchGames, setMatchGames] = useState<Core.Models.MatchGame[] | undefined>(undefined);
    const [accessCode, setAccessCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    const loadGames = useCallback(async () => {
        try {
            const data = await MatchService.getMatchGames(match.id);
            setMatchGames(orderBy(data, (mg: Core.Models.MatchGame) => mg.sortOrder));
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    }, [match.id, setError, setIsLoading]);

    useEffect(() => {
        void loadGames();
    }, [loadGames]);

    useEffect(() => {
        if (!league?.id) return;
        let listenerId: string | null = null;
        (async () => {
            listenerId = await PlatformEventsService.startListening(
                [MATCH_GAME_METADATA_UPDATED, MATCH_GAME_RESULTS_SUBMITTED, MATCH_RESULTS_SUBMITTED],
                league.id
            );
        })();
        return () => {
            (async () => {
                if (listenerId) {
                    await PlatformEventsService.stopListening(listenerId, [
                        MATCH_GAME_METADATA_UPDATED,
                        MATCH_GAME_RESULTS_SUBMITTED,
                        MATCH_RESULTS_SUBMITTED,
                    ]);
                }
            })();
        };
    }, [league?.id]);

    useEffect(() => {
        const subscription = PlatformEventsService.dataReceived.subscribe(
            ({ eventName, data }: { eventName: string; data: any }) => {
                if (
                    [MATCH_GAME_METADATA_UPDATED, MATCH_GAME_RESULTS_SUBMITTED, MATCH_RESULTS_SUBMITTED].includes(
                        eventName
                    ) &&
                    data === match.id
                ) {
                    void loadGames();
                    void reloadMatchParticipants();
                    void reloadMatchData();
                }
            }
        );
        return () => subscription.unsubscribe();
    }, [loadGames, match.id, reloadMatchData, reloadMatchParticipants]);

    const matchGame = matchGames?.[0];
    const launchUrl = useMemo(() => {
        if (!match.titleMetadata) return undefined;
        try {
            return (JSON.parse(match.titleMetadata) as { launchUrl?: string }).launchUrl;
        } catch {
            return undefined;
        }
    }, [match.titleMetadata]);

    const metadata = useMemo(
        () => (matchGame ? parseQuizWhizzerTitleMetadata(matchGame.titleMetadata) : null),
        [matchGame]
    );
    const needsAccessCode = matchGame ? quizWhizzerMatchGameNeedsAccessCode(matchGame) : true;
    const joinLinks = metadata?.joinLinks ?? [];
    const configuredCode = metadata?.accessCode;

    const myMatchParticipantIds = useMemo(
        () =>
            matchParticipants
                .filter((p) => p.users.some((u) => u.userId === userId))
                .map((p) => p.id),
        [matchParticipants, userId]
    );

    const myJoinLink = useMemo(() => {
        if (!myMatchParticipantIds.length || !joinLinks.length) return undefined;
        const mine = new Set(myMatchParticipantIds);
        return joinLinks.find((l) => mine.has(l.matchParticipantId));
    }, [joinLinks, myMatchParticipantIds]);

    const participantScores = useMemo(() => {
        if (!matchGame?.isComplete || !matchGame.results?.length) return [];
        const latest = orderBy(matchGame.results, (r) => r.submittedTimeUtc, 'desc')[0];
        const scores = latest?.scores ?? [];
        const rows = matchParticipants.map((participant) => {
            const user = orderBy(participant.users, (u) => u.sortOrder)[0];
            const score = scores.find((s) => s.matchParticipantId === participant.id)?.score;
            return {
                id: participant.id,
                name: getParticipantDisplayName(participant),
                avatarUrl: user?.avatarUrl || participant.avatarUrl,
                score: typeof score === 'number' ? score : undefined,
            };
        });
        return orderBy(rows, [(r) => typeof r.score === 'number', (r) => r.score ?? -1], ['desc', 'desc']);
    }, [matchGame, matchParticipants]);

    const refreshAll = useCallback(async () => {
        await loadGames();
        await reloadMatchParticipants();
        await reloadMatchData();
    }, [loadGames, reloadMatchData, reloadMatchParticipants]);

    const submitAccessCode = useCallback(async () => {
        if (!matchGame || !accessCode.trim() || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await MatchService.applyMatchGameTitleSetup({
                matchId: match.id,
                matchGameId: matchGame.id,
                configuration: { accessCode: accessCode.trim() },
            });
            toast.success('Access code saved — student join links are ready.');
            setAccessCode('');
            await refreshAll();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsSubmitting(false);
        }
    }, [accessCode, isSubmitting, match.id, matchGame, refreshAll]);

    const resetSetup = useCallback(async () => {
        if (!matchGame || isResetting) return;
        if (!window.confirm('Reset QuizWhizzer setup? This clears the access code and join links.')) return;
        setIsResetting(true);
        try {
            await MatchService.resetMatchGameTitleSetup({ matchId: match.id, matchGameId: matchGame.id });
            toast.success('Setup reset — paste a new access code.');
            await refreshAll();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsResetting(false);
        }
    }, [isResetting, match.id, matchGame, refreshAll]);

    const importScores = useCallback(async () => {
        if (!matchGame || isImporting) return;
        setIsImporting(true);
        try {
            await MatchService.submitMatchGameResult({
                matchGameId: matchGame.id,
                autoScoringData: JSON.stringify({ pull: true }),
                scores: [],
                screenshots: [],
            });
            toast.success(
                'Import started — end the QuizWhizzer game first if it is still live; scores import when status is FINISHED.'
            );
            await refreshAll();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsImporting(false);
        }
    }, [isImporting, matchGame, refreshAll]);

    const copyAllLinks = useCallback(async () => {
        if (!joinLinks.length) return;
        const text = joinLinks.map((l) => `${l.name}: ${l.joinUrl}`).join('\n');
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Join links copied.');
        } catch {
            toast.error('Could not copy join links.');
        }
    }, [joinLinks]);

    if (!matchGames) return <></>;

    const status =
        matchGame?.isComplete
            ? { color: 'default' as const, label: 'Results imported' }
            : !needsAccessCode && configuredCode
              ? { color: 'success' as const, label: 'Ready to play' }
              : { color: 'warning' as const, label: canConfigureAsHost ? 'Needs access code' : 'Waiting on professor' };

    return (
        <ContentContainer
            className="class-session-game shadow-medium"
            shade={Core.Models.Shades.Dark20}
            radius={Core.Models.RadiusSizes.Large}
        >
            <div className="class-session-game__header">
                <div>
                    <p className="class-session-game__kicker">QuizWhizzer</p>
                    <h3 className="class-session-game__heading">Play the game</h3>
                </div>
                <StatusIndicator color={status.color} outlineOnly>
                    {status.label}
                </StatusIndicator>
            </div>

            <div className="class-session-game__body">
                <p className="class-session-game__lede">
                    {canConfigureAsHost
                        ? 'This is where you connect LeagueSpot to the live QuizWhizzer game. Students join QuizWhizzer from here after you paste the access code.'
                        : 'This is where you open the live QuizWhizzer game. Your professor sets it up first; then your personal join link appears below.'}
                </p>

                {canConfigureAsHost && needsAccessCode && (
                    <ol className="class-session-game__steps">
                        <li>
                            <span className="class-session-game__step-label">Step 1</span>
                            <div className="class-session-game__step-content">
                                <p className="class-session-game__step-title">Start the game in QuizWhizzer</p>
                                <p className="class-session-game__step-copy">
                                    {launchUrl
                                        ? 'Open the launch link for this topic, start the QuizWhizzer game, and copy the access code it shows.'
                                        : 'Create and start the game in QuizWhizzer, then copy the access code.'}
                                </p>
                                {!!launchUrl && (
                                    <HollowButton
                                        as="a"
                                        href={launchUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        size="small"
                                    >
                                        Open in QuizWhizzer
                                    </HollowButton>
                                )}
                            </div>
                        </li>
                        <li>
                            <span className="class-session-game__step-label">Step 2</span>
                            <div className="class-session-game__step-content">
                                <p className="class-session-game__step-title">Paste the access code here</p>
                                <p className="class-session-game__step-copy">
                                    Saving the code generates a locked join link for every student in the lobby.
                                </p>
                                <div className="class-session-game__code-row">
                                    <TextInput
                                        className="class-session-game__code-input"
                                        disabled={isSubmitting}
                                        label="QuizWhizzer access code"
                                        onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                                        value={accessCode}
                                    />
                                    <SolidButton
                                        as="button"
                                        disabled={isSubmitting || !accessCode.trim()}
                                        onClick={() => void submitAccessCode()}
                                        pending={isSubmitting}
                                        size="medium"
                                    >
                                        Save code
                                    </SolidButton>
                                </div>
                            </div>
                        </li>
                    </ol>
                )}

                {!canConfigureAsHost && needsAccessCode && (
                    <div className="class-session-game__waiting">
                        <p className="class-session-game__waiting-title">Waiting for your professor</p>
                        <p className="class-session-game__waiting-copy">
                            Stay on this page. When they paste the QuizWhizzer access code, your personal join button
                            will show up here.
                        </p>
                    </div>
                )}

                {!needsAccessCode && configuredCode && (
                    <>
                        {canConfigureAsHost && (
                            <div className="class-session-game__host-live">
                                <div className="class-session-game__code-pill">
                                    Access code <strong>{configuredCode}</strong>
                                </div>
                                <p className="class-session-game__step-copy">
                                    Students can join QuizWhizzer with their locked names. When the QuizWhizzer game
                                    finishes, import scores to update the lobby standings.
                                </p>
                                <div className="class-session-game__actions">
                                    {canImportScores && !matchGame?.isComplete && (
                                        <SolidButton
                                            as="button"
                                            onClick={() => void importScores()}
                                            pending={isImporting}
                                            size="medium"
                                        >
                                            Import QuizWhizzer scores
                                        </SolidButton>
                                    )}
                                    <HollowButton
                                        as="button"
                                        disabled={isResetting}
                                        onClick={() => void resetSetup()}
                                        size="medium"
                                    >
                                        {isResetting ? 'Resetting…' : 'Reset setup'}
                                    </HollowButton>
                                    {joinLinks.length > 0 && (
                                        <HollowButton as="button" onClick={() => void copyAllLinks()} size="medium">
                                            Copy all join links
                                        </HollowButton>
                                    )}
                                </div>
                                {joinLinks.length > 0 && (
                                    <ul className="class-session-game__links">
                                        {joinLinks.map((link) => {
                                            const participant = matchParticipants.find(
                                                (p) => p.id === link.matchParticipantId
                                            );
                                            const label = participant
                                                ? getParticipantDisplayName(participant)
                                                : link.teamName || 'Student';
                                            return (
                                                <li key={link.matchParticipantId}>
                                                    <div>
                                                        <strong>{label}</strong>
                                                        <span className="class-session-game__locked-name">
                                                            locked name: {link.name}
                                                        </span>
                                                    </div>
                                                    <a href={link.joinUrl} rel="noreferrer" target="_blank">
                                                        Open join link
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )}

                        {!canConfigureAsHost && (
                            <div className="class-session-game__student-live">
                                {myJoinLink ? (
                                    <>
                                        <p className="class-session-game__waiting-title">Your QuizWhizzer join link</p>
                                        <p className="class-session-game__waiting-copy">
                                            Join with your locked name (<strong>{myJoinLink.name}</strong>) so scores
                                            match correctly.
                                        </p>
                                        <SolidButton
                                            as="a"
                                            href={myJoinLink.joinUrl}
                                            rel="noreferrer"
                                            size="large"
                                            target="_blank"
                                        >
                                            Join QuizWhizzer
                                        </SolidButton>
                                    </>
                                ) : isMyMatch ? (
                                    <p className="class-session-game__waiting-copy">
                                        Your join link isn’t ready yet. Ask your professor if the access code has been
                                        saved.
                                    </p>
                                ) : (
                                    <p className="class-session-game__waiting-copy">
                                        Join the session lobby above to get a QuizWhizzer link.
                                    </p>
                                )}
                            </div>
                        )}
                    </>
                )}

                {participantScores.length > 0 && matchGame?.isComplete && (
                    <div className="class-session-game__results">
                        <div className="class-session-game__results-header">
                            <h4 className="class-session-game__results-heading">Standings</h4>
                            <p className="class-session-game__results-sub">Imported from QuizWhizzer</p>
                        </div>
                        <ol className="class-session-game__results-list">
                            {participantScores.map((row, index) => (
                                <li
                                    key={row.id}
                                    className={
                                        index === 0
                                            ? 'class-session-game__results-item class-session-game__results-item--first'
                                            : 'class-session-game__results-item'
                                    }
                                >
                                    <span className="class-session-game__place">{index + 1}</span>
                                    <Avatar fallback="user" size="small" src={row.avatarUrl} />
                                    <span className="class-session-game__results-name">{row.name}</span>
                                    <strong className="class-session-game__results-score">
                                        {typeof row.score === 'number' ? row.score : '—'}
                                    </strong>
                                </li>
                            ))}
                        </ol>
                    </div>
                )}
            </div>
        </ContentContainer>
    );
};

export default withLoading(ClassSessionGamePanel, {
    loadingProps: { blockItem: true },
});
